/**
 * useGetNeighborhoods Hook
 *
 * A flexible React Query hook for fetching neighborhoods data with full control over API parameters.
 *
 * @example
 * // Fetch all neighborhoods without pagination (returns direct array)
 * const {neighborhoods, isLoading, error} = useGetNeighborhoods({
 *   geoCode: 'Rome',
 *   responseType: 'extended'
 * }, {enabled: !!geoCode})
 *
 * @example
 * // Fetch neighborhoods with pagination
 * const {neighborhoods, isLoading, error} = useGetNeighborhoods({
 *   geoCode: 'Rome',
 *   responseType: 'small',
 *   limit: 50,
 *   page: 1
 * }, {enabled: !!geoCode})
 *
 * @param promiseArgs - Configuration object with geoCode, responseType, and optional pagination params
 * @param options - React Query options (enabled, etc.)
 * @returns Query result with transformed neighborhoods data
 */

import { useQuery } from '@tanstack/react-query'
import {
	type HttpGetNeighborhoodsParams,
	httpGetNeighborhoods
} from '@/features/subgeo/http/get-neighborhoods.http'
import { transformApiNeighborhoodToNeighborhood } from '@/features/subgeo/types'
import { QUERY_KEYS } from '@/query-client'

import type { ResponseType } from '@/types'

interface UseGetNeighborhoodsParams {
	geoCode: string
	responseType?: ResponseType
	limit?: number
	page?: number
	name?: string
}

export const useGetNeighborhoods = (
	promiseArgs: UseGetNeighborhoodsParams,
	options?: { enabled?: boolean }
) => {
	const params: HttpGetNeighborhoodsParams = {
		responseType: promiseArgs.responseType || 'extended',
		geoCode: promiseArgs.geoCode,
		...(promiseArgs.limit && { limit: promiseArgs.limit }),
		...(promiseArgs.page && { page: promiseArgs.page }),
		...(promiseArgs.name && { name: promiseArgs.name })
	}

	const query = useQuery({
		queryKey: [QUERY_KEYS.GET_NEIGHBORHOODS, params],
		queryFn: () => httpGetNeighborhoods(params),
		enabled: options?.enabled && !!params.geoCode,
		select: (data) => ({
			...data,
			neighborhoods: data.data.map(transformApiNeighborhoodToNeighborhood)
		})
	})

	return {
		...query,
		neighborhoods: query.data?.neighborhoods ?? []
	}
}
